import React from 'react';
import { ComponentConfig } from '@measured/puck';
import { TextWithTooltip } from './HoverTooltip';
import { marginFields, MarginProps } from '../../puck/reusable-props/margin';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import {
  getMarginStyles,
  getPaddingStyles,
  getBorderProps,
  getcurrentDeviceType,
} from '../../utils/helper';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';

export type HoverTooltipProps = {
  label: string;
  tooltipText: string;
  tooltipTextAlign: string;
  tooltipIconSize: number;
  tooltipIconColor: string;
  tooltipShowArrow: boolean;
  tooltipBackground: string;
  border: IBorderValueProps;
} & MarginProps &
  PaddingProps &
  showComponentProps;

export const HoverTooltip = ({
  useAppSelector,
}: ReduxDispatchProps &
  ReduxSelectorProps): ComponentConfig<HoverTooltipProps> => {
  return {
    label: (
      <CustomTooltip title="Hover Tooltip">
        <span>Hover Tooltip</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      label: {
        ...QuillTextEditor('Label Settings', true, 0).content,
      },
      tooltipText: {
        ...QuillTextEditor('Tooltip Settings', true, 1).content,
      },
      tooltipTextAlign: {
        type: 'radio',
        label: 'Tooltip Text Align',
        options: [
          { label: 'Left', value: 'flex-start' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'flex-end' },
        ],
      },
      tooltipBackground: {
        type: 'custom',
        label: 'Tooltip Background Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      tooltipIconSize: {
        type: 'number',
        label: 'Icon Size',
        min: 16,
        max: 100,
      },
      tooltipIconColor: {
        type: 'custom',
        label: 'Tooltip Icon Color',
        render: ({ value, onChange, field }) => (
          <ColorPicker
            value={value}
            onChange={onChange}
            label={field.label}
            tooltip={field.label}
          />
        ),
      },
      tooltipShowArrow: {
        type: 'radio',
        label: 'Show Arrow',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
    },

    defaultProps: {
      label: '<p>Estimated Tax</p>',
      tooltipText: '<p>This includes all taxes and discounts.</p>',
      tooltipTextAlign: 'flex-start',
      tooltipBackground: '#000000',
      tooltipIconSize: 18,
      tooltipIconColor: '#666666',
      tooltipShowArrow: true,
      ...borderProps,
      margin: {
        desktop: { top: '0', bottom: '0', left: '0', right: '0' },
        tablet: { top: '0', bottom: '0', left: '0', right: '0' },
        mobile: { top: '0', bottom: '0', left: '0', right: '0' },
      },
      padding: {
        deviceType: 'desktop',
        paddingTop: { desktop: '0', tablet: '0', mobile: '0' },
        paddingBottom: { desktop: '0', tablet: '0', mobile: '0' },
        paddingLeft: { desktop: '0', tablet: '0', mobile: '0' },
        paddingRight: { desktop: '0', tablet: '0', mobile: '0' },
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
    },

    render: ({
      label,
      tooltipText,
      tooltipTextAlign,
      tooltipIconSize,
      tooltipIconColor,
      tooltipShowArrow,
      tooltipBackground,
      margin,
      padding,
      border,
      showComponent,
      editMode,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      if (!showComponent?.includes(currentDeviceType)) return <></>;

      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const borderStyles = getBorderProps(border, currentDeviceType);

      return (
        <div
          className={border?.borderStyle?.[currentDeviceType]}
          style={{
            ...marginStyles,
            ...paddingStyles,
            ...borderStyles,
          }}>
          <TextWithTooltip
            label={label}
            tooltipText={tooltipText}
            tooltipTextAlign={tooltipTextAlign}
            tooltipIconSize={tooltipIconSize}
            tooltipIconColor={tooltipIconColor}
            tooltipShowArrow={tooltipShowArrow}
            tooltipBackground={tooltipBackground}
          />
        </div>
      );
    },
  };
};
